import { useCallback, useMemo, useState } from 'react';
import { P2Accordion, P2Alert, P2Button, P2TextDescription } from 'react-bootstrap-common-script';
import { Separator } from '../../../components/separator';
import FinancialPlan from '../component/financial-plan';
import { StageEInfoCard } from '../../../components/stage-e-info-card';
import { Helpers } from '../../../utils/helper';
import { PaymentCardIcon } from '../../../assets/svg';
import { useAppSelector } from '../../../redux/hook';
import { StructureStatusEnum } from '../../../models/enum/structure-status.enum';
import { useGetCompletionPaymentDetailsQuery } from '../../../redux/services/getStageE';
import { useGetCurrentFinancialPlanDataQuery, useGetProposedFinancialPlanDataQuery } from '../../../redux/services/getFinancialPlanData';
import { useGetApplicationUuid } from '../../../hooks/useGetApplicationUuid';

const StageE2PaymentSegment = () => {
  const [accordionSubtitle, setAccordionSubtitle] = useState<boolean>(true);
  const handleAccordionSubtitle = useCallback(() => {
    setAccordionSubtitle((prev) => !prev);
  }, []);

  const applicationUuid = useGetApplicationUuid();
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const applicationStep1 = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);

  const { data: completionPaymentDetails } = useGetCompletionPaymentDetailsQuery({
    application_id: applicationUuid ?? '0',
    uinfin: loginUser?.uinfin ?? '',
    singpass_token: singpassToken ?? '0',
  });

  // Determine structure status for the logged-in user
  const struct = useMemo(() => {
    const owners = [...(applicationStep1?.household?.incoming_owners ?? []), ...(applicationStep1?.household?.owners ?? [])];

    return owners.find((o) => o?.myinfo?.uinfin === loginUser?.uinfin)?.structure_status;
  }, [applicationStep1, applicationStep1?.household, loginUser?.uinfin]);

  const { data: currentFinancialPlanData } = useGetCurrentFinancialPlanDataQuery(
    {
      application_id: applicationUuid ?? '0',
      uinfin: loginUser?.uinfin ?? '',
      singpass_token: singpassToken ?? '0',
    },
    { skip: struct === StructureStatusEnum.incoming }
  );

  const { data: proposedFinancialPlanData } = useGetProposedFinancialPlanDataQuery(
    {
      application_id: applicationUuid ?? '0',
      uinfin: loginUser?.uinfin ?? '',
      singpass_token: singpassToken ?? '0',
    },
    { skip: struct === StructureStatusEnum.outgoing }
  );

  const currentOwnerSolicitorFee = currentFinancialPlanData?.total_solicitor_fee_by_cash ?? 0;
  const proposedOwnerSolicitorFee = proposedFinancialPlanData?.total_solicitor_fee_by_cash ?? 0;

  const includeSolicitorFee = useMemo(() => {
    switch (struct) {
      case StructureStatusEnum.outgoing:
        return currentOwnerSolicitorFee > 0;

      case StructureStatusEnum.incoming:
        return proposedOwnerSolicitorFee > 0;

      case StructureStatusEnum.retaining:
        return currentOwnerSolicitorFee > 0 || proposedOwnerSolicitorFee > 0;

      default:
        return false;
    }
  }, [struct, currentOwnerSolicitorFee, proposedOwnerSolicitorFee]);

  const generatePaymentCard = (ownerType: 'current_owner' | 'proposed_owner', label: string) => {
    if (!completionPaymentDetails) return null;

    const paymentDetails = completionPaymentDetails.cash_payment?.[ownerType];
    if (!paymentDetails) return null;

    const { amount = 0, paid_on, payment_mode } = paymentDetails;

    const solicitorFee = ownerType === 'current_owner' ? currentOwnerSolicitorFee : proposedOwnerSolicitorFee;
    const totalPayment = amount + solicitorFee;

    if (totalPayment === 0) {
      // Scenario: Cash payment amount equals 0.
      return (
        <StageEInfoCard
          variant='success'
          cardLabel={`Amount paid by ${label}`}
          title='$0.00'
          description='Required payment has been made via CPF withdrawal or loan. No further payments are required.'
          icon={<PaymentCardIcon />}
        />
      );
    }

    if (paid_on) {
      // Scenario: Cash payment amount > 0 and PAID.
      return (
        <StageEInfoCard
          variant='success'
          cardLabel={`Payment for ${label} is successful`}
          title={`$${Helpers.formatThousandSeparator(totalPayment)}`}
          paidOn={paid_on}
          paidBy={payment_mode}
        />
      );
    }

    // Scenario: Cash payment amount > 0 and NOT paid.
    return (
      <StageEInfoCard
        variant='info'
        cardLabel={`Amount to be paid by ${label}`}
        title={`$${Helpers.formatThousandSeparator(totalPayment)}`}
        // R2 Description
        description={'You can pay on completion appointment day using cash, cashier’s order, and AXS, subject to standard payment limits.'}
        // R2-Batch-1 Description
        // description={
        //   <>
        //     <p>You can pay on completion appointment day using cash, cashier's order, and AXS, subject to standard payment limits.</p>
        //     <br />
        //     <p>Participating banks for Direct debit: DBS/POSB, OCBC Bank, Standard Chartered Bank, and United Overseas Bank Limited</p>
        //     <br />
        //     <p>
        //       Participating banks for Paynow: Bank of China, Citibank, DBS/POSB, HSBC Bank, Industrial and Commercial Bank of China Limited, Maybank,
        //       OCBC Bank, Standard Chartered Bank, and United Overseas Bank Limited
        //     </p>
        //     <p>* Do not make payment via Bank's website by keying in HDB's UEN. HDB accepts only payment via QR code generated from the eServices.</p>
        //     <br />

        //     <div className='d-flex gap-2'>
        //       <P2Button
        //         as='a'
        //         variant={'outline-primary'}
        //         onClick={() => {
        //           window.open('https://services2.hdb.gov.sg/webapp/AC28AWEPAYMENT/AC28EPAYMENTDDmain.jsp', '_blank');
        //         }}>
        //         Direct Debit
        //       </P2Button>
        //       <P2Button
        //         variant={'outline-primary'}
        //         onClick={() => {
        //           window.open('https://services2.hdb.gov.sg/webapp/AC28AWEPAYMENT/AC28EPAYMENTPNMain.jsp', '_blank');
        //         }}>
        //         PayNow
        //       </P2Button>
        //     </div>
        //   </>
        // }
        icon={<PaymentCardIcon />}
      />
    );
  };

  return (
    <>
      <div className='polymer-stage-e-accordion'>
        <P2Accordion
          defaultOpen={true}
          hasDivider={true}
          onToggle={handleAccordionSubtitle}
          title={
            <div className='polymer-accordion-listing_title'>
              <div className='polymer-accordion-listing_title_wrap'>
                <h4>1. Required payment</h4>
              </div>
              {accordionSubtitle && (
                <div className='polymer-stage-e-accordion-subtitle'>
                  <P2TextDescription color='#333' description={'Based on the confirmed financial plan, the following payments are required.'} />
                </div>
              )}
            </div>
          }>
          {/* Accordion's Content Starts Here --- */}
          <FinancialPlan />
          <Separator style={{ margin: '16px 0' }} />

          {/* Card */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div>
              {(struct === StructureStatusEnum.outgoing || struct === StructureStatusEnum.retaining) && (
                <div> {generatePaymentCard('current_owner', 'current owners')}</div>
              )}
            </div>
            <div>
              {(struct === StructureStatusEnum.incoming || struct === StructureStatusEnum.retaining) && (
                <div> {generatePaymentCard('proposed_owner', 'proposed owners')}</div>
              )}
            </div>

            {includeSolicitorFee && (
              <div className='mt-2'>
                <P2Alert
                  variant={'info'}
                  withBorder={true}
                  text={'The total amount payable includes the amount that private lawyer(s) are required to pay.'}
                />
              </div>
            )}
          </div>
        </P2Accordion>
      </div>
    </>
  );
};

export default StageE2PaymentSegment;
